import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { sessionsApi } from '../../../../../api/sessionsApi';
import { getTranscriptionLanguageName } from '../../../../../constants/transcriptionLanguages';
import { formatDateTime, formatDuration } from '../../../../../utils/format';
import { reportModelLabel } from '../../../report/utils/reportConfig';

const secondsBetween = (start, end) => (start && end
  ? Math.max(0, Math.round((new Date(end) - new Date(start)) / 1000))
  : null);

const took = (value) => (value != null ? formatDuration(value, '0s') : null);
const when = (value) => (value ? formatDateTime(value) : null);

function KeyValueCard({ title, rows, children }) {
  const visible = rows.filter(([, value]) => value !== null && value !== undefined && value !== '');
  if (visible.length === 0 && !children) return null;
  return (
    <div className="session-detail-card">
      <h3>{title}</h3>
      <div className="session-kv-list">
        {visible.map(([label, value]) => (
          <div key={label}><span>{label}</span><strong>{value}</strong></div>
        ))}
      </div>
      {children}
    </div>
  );
}

/** Everything known about the session that is not report content. */
export default function DetailsTab({ session, reportStatus, wordCount, notify }) {
  const [isDownloading, setIsDownloading] = useState(false);
  const reportModel = reportStatus?.used_model;

  const downloadMedia = async () => {
    setIsDownloading(true);
    try {
      await sessionsApi.downloadMedia(session.id);
      notify({ type: 'success', title: 'Download started', message: `Downloading ${session.original_filename || 'media file'}` });
    } catch (e) {
      notify({ type: 'error', title: 'Failed to download media', message: e.message || 'Media file not available' });
    } finally {
      setIsDownloading(false);
    }
  };

  const event = session.event_id && session.event_title
    ? <Link to={`/events/${session.event_id}`} className="session-detail-event-link">{session.event_title}</Link>
    : session.event_title;

  return (
    <div className="session-details-grid">
      <KeyValueCard
        title="Session"
        rows={[
          ['Event', event],
          ['User', session.user_name || session.user_email],
          ['WordPress session', session.wp_session_id],
          ['FAICON session', session.faicon_session_id],
          ['Published URL', session.url && <a href={session.url} target="_blank" rel="noopener noreferrer">{session.url}</a>],
          ['Created', when(session.created_at)],
          ['Updated', when(session.updated_at)],
        ]}
      />
      <KeyValueCard
        title="Processing"
        rows={[
          ['Transcript provider', session.transcript_provider],
          ['Language', session.transcript_language && getTranscriptionLanguageName(session.transcript_language)],
          ['Words', wordCount > 0 ? `~ ${wordCount.toLocaleString()}` : null],
          ['Transcript started', when(session.transcript_processing_started_at)],
          ['Transcript took', took(secondsBetween(session.processing_started_at, session.processing_completed_at))],
          ['Report model', reportModel && reportModelLabel(reportModel)],
          ['Report took', took(reportStatus?.processing_duration)],
        ]}
      />
      <KeyValueCard
        title="File"
        rows={[
          ['Filename', session.original_filename],
          ['Duration', formatDuration(session.duration_seconds, null)],
        ]}
      >
        <button
          type="button"
          className="session-btn session-btn-secondary session-btn-full"
          onClick={downloadMedia}
          disabled={isDownloading}
        >
          <ArrowDownTrayIcon />
          {isDownloading ? 'Downloading...' : 'Download video / audio'}
        </button>
      </KeyValueCard>
    </div>
  );
}
